import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Lightbulb,
  CheckCircle,
  Target,
  Award,
  Brain,
  Eye,
  EyeOff,
  Sparkles
} from 'lucide-react';
import { shareApi } from '../../services/shareApi';
import { SharedExam, SharedExamQuestion, SharedExamHint, SharedExamSolution } from '../../types/share.types';
import StudyMaterialRenderer from '../StudyMaterialRenderer';
import { MixedText } from '../../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { PageContainer } from '../layout/PageContainer';
import RestrictedChatButton from './RestrictedChatButton';

const SharedExamView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he';

  const [exam, setExam] = useState<SharedExam | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  // Local state for visibility to mimic the interactive experience
  const [showHints, setShowHints] = useState<{[questionId: number]: number}>({});
  const [showSolution, setShowSolution] = useState<{[questionId: number]: boolean}>({});
  const [solutionFontSize, setSolutionFontSize] = useState(16);

  useEffect(() => {
    const fetchExam = async () => {
      if (!token) {
        setError(t('common:errors.not_found', 'Shared exam not found'));
        setLoading(false);
        return;
      }
      try {
        const data = await shareApi.getSharedExam(token);
        setExam(data);
      } catch (err) {
        setError(t('common:errors.loading_failed', 'Unable to load shared exam.'));
      } finally {
        setLoading(false);
      }
    };
    fetchExam();
  }, [token, t]);

  const questions = exam?.questions || [];
  const currentQuestion = questions[currentQuestionIndex];
  const canGoBack = currentQuestionIndex > 0;
  const canGoForward = currentQuestionIndex < questions.length - 1;

  const navigateToQuestion = (newIndex: number) => {
    if (newIndex >= 0 && newIndex < questions.length) {
      setCurrentQuestionIndex(newIndex);
    }
  };

  const toggleHint = (questionId: number, level: number) => {
    setShowHints(prev => ({
      ...prev,
      [questionId]: level
    }));
  };

  const toggleSolution = (questionId: number) => {
    setShowSolution(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  // Derived state for current question
  const showingHints = currentQuestion ? showHints[currentQuestion.id] || 0 : 0;
  const showingSolution = currentQuestion ? showSolution[currentQuestion.id] || false : false;
  const currentQuestionHints = currentQuestion?.hints || [];
  // Since SharedExamQuestion has solutions as an array, but Practice view treats it as single object sometimes or list.
  // SharedExamSolution[] type suggests multiple solutions possible, but Practice view UI usually shows one text.
  // Let's look at Practice View: it uses `ExamSolution` which seems to be a single object or it maps.
  // In SharedExamView type: `solutions: SharedExamSolution[]`.
  // Let's combine them or show the first one if multiple, or map them.
  // Practice View: `solutions[questionId]: ExamSolution`. `content={currentQuestionSolution.solution_text}`
  // We'll assume we want to show all solutions if multiple, or just the first one if that's the model.
  // Let's render them one by one if there are multiple.

  if (loading) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md mx-auto constellation-orion">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <CardTitle className="text-surface-primary mb-2 font-hebrew">
              <MixedText>{t('exams:practice.loading.title', 'Loading exam...')}</MixedText>
            </CardTitle>
            <p className="text-surface-secondary font-hebrew">
              <MixedText>{t('exams:practice.loading.description', 'Preparing your exam view')}</MixedText>
            </p>
          </Card>
        </div>
      </PageContainer>
    );
  }

  if (error || !exam) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md mx-auto">
            <CardTitle className="text-surface-primary mb-4 font-hebrew">
              <MixedText>{error || t('common:errors.not_found', 'Shared exam not found')}</MixedText>
            </CardTitle>
            <Button variant="primary" onClick={() => navigate('/login')}>
              {t('common:navigation.login', 'Log in')}
            </Button>
          </Card>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
      {/* Header */}
      <div className="mb-8">
        <div className="mb-6">
            <button
            onClick={() => navigate('/login')}
            className={`flex items-center text-bg-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
            >
            {isRTL ? (
                <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
            ) : (
                <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
                <MixedText className="text-bg-secondary hover:text-vibrant-orange transition-colors">
                {t('navigation.signup', 'Sign Up')}
                </MixedText>
            </span>
            </button>
        </div>

        <Card className="p-6 constellation-orion bg-gradient-to-br from-muted-purple/20 to-vibrant-orange/10">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-gradient-to-br from-muted-purple to-vibrant-orange rounded-2xl flex items-center justify-center">
                <Target className="w-8 h-8 text-white" />
              </div>
              <div className={`${isRTL ? 'text-right' : ''}`}>
                <h1 className={`text-2xl font-bold text-surface-primary mb-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{exam.title}</MixedText>
                </h1>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('exams:practice.practiceMode', 'Practice mode')}</MixedText>
                </p>
              </div>
            </div>

            <div className={`text-center ${isRTL ? 'text-right' : ''}`}>
              <div className={`text-2xl font-bold text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                {currentQuestionIndex + 1} / {questions.length}
              </div>
              <div className={`text-sm text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{t('exams:practice.questions', 'Questions')}</MixedText>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="w-full bg-surface-subtle rounded-full h-2">
              <div
                className="bg-gradient-to-r from-muted-purple to-vibrant-orange h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${questions.length ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0}%` }}
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Layout Grid */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Read Only / Sign Up CTA */}
            <button
            onClick={() => navigate('/register')}
            className={`p-4 rounded-2xl transition-all duration-200 hover:scale-105 font-hebrew flex items-center justify-center ${
                isRTL ? 'flex-row-reverse' : ''
            } bg-gradient-to-br from-muted-purple/85 to-muted-purple/95 
                hover:from-muted-purple/95 hover:to-muted-purple
                backdrop-blur-sm border border-muted-purple/30 
                shadow-lg hover:shadow-xl hover:shadow-muted-purple/30
                text-astronaut-white hover:text-white`}
            >
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse gap-4' : 'gap-4'}`}>
                <img
                src="/astronaut_cool.png"
                alt="Read only"
                className="w-20 h-20 drop-shadow-[0_6px_12px_rgba(134,116,245,0.35)]"
                />
                <div className="flex flex-col">
                    <span className="text-xl font-semibold flex items-center gap-3">
                        <MixedText>{t('labels.read_only_preview', 'Read-only preview')}</MixedText>
                    </span>
                    <span className="text-sm text-white/80 mt-1">
                        <MixedText>{t('navigation.signup', 'Sign Up')}</MixedText>
                    </span>
                </div>
            </div>
            </button>

            {/* Question Overview */}
            <Card className="p-3 constellation-ursa">
                <CardTitle className={`text-surface-primary mb-2 font-hebrew text-sm ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{`📋 ${t('exams:practice.overview', 'Overview')}`}</MixedText>
                </CardTitle>
                <div className="flex flex-wrap gap-1">
                    {questions.map((question, index) => (
                    <button
                        key={question.id}
                        onClick={() => navigateToQuestion(index)}
                        className={`relative w-8 h-8 rounded-lg border-2 flex items-center justify-center text-xs font-medium transition-all duration-200 hover:scale-105 ${
                        index === currentQuestionIndex
                            ? 'border-vibrant-orange bg-vibrant-orange/20 text-vibrant-orange shadow-md'
                            : 'border-surface-border bg-surface-subtle text-surface-secondary hover:border-surface-primary hover:bg-surface-hover'
                        }`}
                    >
                        {index + 1}
                    </button>
                    ))}
                </div>
            </Card>

            {/* Navigation */}
            <Card className="p-3 constellation-cassiopeia">
                <CardTitle className={`text-surface-primary mb-2 font-hebrew text-sm ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{`🧭 ${t('exams:practice.navigation', 'Navigation')}`}</MixedText>
                </CardTitle>
                <div className="space-y-2">
                    <button
                    onClick={() => navigateToQuestion(currentQuestionIndex - 1)}
                    disabled={!canGoBack}
                    className={`w-full btn-secondary flex items-center justify-center transition-all duration-200 font-hebrew py-2 text-sm ${
                        !canGoBack ? 'opacity-50 cursor-not-allowed' : ''
                    } ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                    <ChevronLeft className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                    <span className="text-sm">
                        <MixedText>{t('exams:practice.previousQuestion', 'Previous Question')}</MixedText>
                    </span>
                    </button>

                    <button
                    onClick={() => navigateToQuestion(currentQuestionIndex + 1)}
                    disabled={!canGoForward}
                    className={`w-full btn-secondary flex items-center justify-center transition-all duration-200 font-hebrew py-2 text-sm ${
                        !canGoForward ? 'opacity-50 cursor-not-allowed' : ''
                    } ${isRTL ? 'flex-row-reverse' : ''}`}
                    >
                    <span className="text-sm">
                        <MixedText>{t('exams:practice.nextQuestion', 'Next Question')}</MixedText>
                    </span>
                    <ChevronRight className={`w-4 h-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                    </button>
                </div>
            </Card>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Question Card */}
        <Card className="constellation-cassiopeia">
          <CardHeader>
            <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
              <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                <MixedText>{`${t('exams:practice.question', 'Question')} ${currentQuestionIndex + 1}`}</MixedText>
              </CardTitle>
              <div className={`flex items-center space-x-4 ${isRTL ? 'space-x-reverse' : ''}`}>
                {currentQuestion?.points && (
                  <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                    <Award className="w-4 h-4 mr-2 ml-2 text-vibrant-orange" />
                    <span className={`text-sm font-medium text-vibrant-orange font-hebrew ${isRTL ? 'text-right' : ''}`}>
                      <MixedText>{`${currentQuestion.points} ${t('common:labels.points', 'Points')}`}</MixedText>
                    </span>
                  </div>
                )}
                {currentQuestion?.difficulty_level && (
                  <div className={`flex items-center px-2 py-1 rounded-full text-xs font-medium bg-surface-subtle text-surface-primary ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span><MixedText>{currentQuestion.difficulty_level}</MixedText></span>
                    <Brain className={`w-3 h-3 ${isRTL ? 'ml-2 mr-2' : 'mr-2 ml-2'}`} />
                  </div>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent>
            <div className={`prose prose-lg max-w-none ${isRTL ? 'text-right' : ''}`}>
              {currentQuestion ? (
                <StudyMaterialRenderer
                  content={currentQuestion.question_text}
                  materialType="question"
                  isRTL={isRTL}
                />
              ) : (
                <p className="text-surface-secondary font-hebrew">
                  <MixedText>{t('exams:practice.loadingQuestion', 'Loading question...')}</MixedText>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Hints and Solution */}
        <div className="space-y-6">
          {/* Hints Section */}
          <Card className="constellation-ursa">
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`💡 ${t('exams:practice.hints.title', 'Hints')}`}</MixedText>
                </CardTitle>
                <div className={`flex items-center gap-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                  {currentQuestion && (
                    <button
                      onClick={() => toggleHint(currentQuestion.id, 1)}
                      className={`btn-secondary text-sm flex items-center transition-all duration-200 font-hebrew ${
                        showingHints >= 1 ? 'bg-muted-purple/20 border-muted-purple/30 text-muted-purple' : ''
                      }`}
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      <MixedText>
                        {showingHints >= 1
                          ? t('exams:practice.hints.firstShown', 'First Hint')
                          : t('exams:practice.hints.getFirst', 'Get First Hint')
                        }
                      </MixedText>
                    </button>
                  )}
                  {currentQuestion && currentQuestionHints.length > 1 && showingHints >= 1 && (
                    <button
                      onClick={() => toggleHint(currentQuestion.id, 2)}
                      className={`btn-secondary text-sm flex items-center transition-all duration-200 font-hebrew ${
                        showingHints >= 2 ? 'bg-muted-purple/20 border-muted-purple/30 text-muted-purple' : ''
                      }`}
                    >
                      <Lightbulb className="w-4 h-4 mr-2" />
                      <MixedText>
                        {showingHints >= 2
                          ? t('exams:practice.hints.secondShown', 'Second Hint')
                          : t('exams:practice.hints.getSecond', 'Get Second Hint')
                        }
                      </MixedText>
                    </button>
                  )}
                </div>
              </div>
            </CardHeader>

            <CardContent>
              {showingHints === 0 ? (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-surface-muted mx-auto mb-4 transition-all duration-300 hover:text-vibrant-orange" />
                </div>
              ) : (
                <div className="space-y-4">
                  {currentQuestionHints.slice(0, showingHints).map((hint, index) => (
                    <div
                      key={hint.id}
                      className="p-4 bg-muted-purple/10 rounded-lg border border-muted-purple/20 transition-all duration-300 hover:bg-muted-purple/15"
                      style={{ animationDelay: `${index * 150}ms` }}
                    >
                      <div className={`flex items-start ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-6 h-6 bg-muted-purple rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-bold">{index + 1}</span>
                        </div>
                        <div className="flex-1">
                          <StudyMaterialRenderer
                            content={hint.hint_text}
                            materialType="hint"
                            isRTL={isRTL}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Solution Section */}
          <Card className="constellation-orion">
            <CardHeader>
              <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
                <CardTitle className={`text-surface-primary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{`✅ ${t('exams:practice.solution.title', 'Solution')}`}</MixedText>
                </CardTitle>
                {currentQuestion && (
                  <button
                    onClick={() => toggleSolution(currentQuestion.id)}
                    className={`btn-primary text-sm flex items-center transition-all duration-200 font-hebrew ${
                      showingSolution ? 'bg-vibrant-orange/90 hover:bg-vibrant-orange' : ''
                    }`}
                  >
                    {showingSolution ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-2" />
                        <MixedText>{t('exams:practice.solution.hide', 'Hide Solution')}</MixedText>
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-2" />
                        <MixedText>{t('exams:practice.solution.show', 'Show Solution')}</MixedText>
                      </>
                    )}
                  </button>
                )}
              </div>
            </CardHeader>

            <CardContent>
              {!showingSolution ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-surface-muted mx-auto mb-4 transition-all duration-300 hover:text-vibrant-orange" />
                </div>
              ) : (
                <div className="space-y-4">
                    {currentQuestion.solutions && currentQuestion.solutions.length > 0 ? (
                        currentQuestion.solutions.map((solution) => (
                            <div
                                key={solution.id}
                                className="p-4 bg-muted-purple/10 rounded-lg border border-muted-purple/20 transition-all duration-300 hover:bg-muted-purple/15"
                            >
                            <StudyMaterialRenderer
                                content={solution.solution_text}
                                materialType="solution"
                                isRTL={isRTL}
                                fontSize={solutionFontSize}
                                onFontSizeChange={setSolutionFontSize}
                            />
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-8">
                            <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                                <MixedText>{t('common:messages.no_data', 'No solution available')}</MixedText>
                            </p>
                        </div>
                    )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-10 flex items-center justify-center gap-3">
        <Button
          variant="primary"
          icon={<Sparkles className="w-4 h-4" />}
          onClick={() => navigate('/register')}
        >
          <MixedText>{t('navigation.signup', 'Sign Up')}</MixedText>
        </Button>
        <Button variant="secondary" onClick={() => navigate('/login')}>
          {t('navigation.login', 'Log In')}
        </Button>
      </div>
      <RestrictedChatButton />
    </PageContainer>
  );
};

export default SharedExamView;
